import * as React from "react";

import { AppContext } from "@1js/acui-context";
import { ribbonExperiments } from "@1js/acui-experiments";
import { Async } from "@fluentui/react/lib/Utilities";

import { RibbonControlGroupMLR } from "../RibbonControlGroupMLR";
import { RibbonControlGroupSLR } from "../RibbonControlGroupSLR";
import type { RibbonControlGroupProps } from "./RibbonControlGroup.Props";
import { getRibbonControlGroupClassNames } from "./RibbonControlGroup.styles";

export interface RibbonControlGroupBase {
  containsFocus(): boolean;
  focus(): void;
  hasContainer(): boolean;
}

export class RibbonControlGroup extends React.PureComponent<
  RibbonControlGroupProps,
  {}
> {
  private group = React.createRef<RibbonControlGroupBase>();
  private focusInControlGroup = false;
  private isFastDisabled: boolean | undefined = undefined;

  // This allows us to access AppContext directly from this.context
  static contextType: any = AppContext;
  declare context: React.ContextType<typeof AppContext>;
  private async = new Async();

  public constructor(props: RibbonControlGroupProps) {
    super(props);
  }

  public render() {
    this.isFastDisabled = this.isFastDisabledActive();
    const classNames = getRibbonControlGroupClassNames();

    return (
      <div
        className={`${classNames.root}${
          this.isFastDisabled ? ` ${classNames.rootFastDisabled}` : ""
        }`}
        aria-disabled={this.isFastDisabled ? true : undefined}
      >
        {this.props.type === "SingleLine" && (
          <RibbonControlGroupSLR
            ref={
              this.props.componentRef ||
              (this.group as React.RefObject<RibbonControlGroupSLR>)
            }
            {...this.props}
            onFocusIn={this.onFocusIn}
            onMouseEnter={this.onMouseIn}
            fastDisabled={
              !this.focusInControlGroup && this.fastDisabledCallback
            }
          />
        )}
        {this.props.type === "Multiline" && (
          <RibbonControlGroupMLR
            ref={
              this.props.componentRef ||
              (this.group as React.RefObject<RibbonControlGroupMLR>)
            }
            {...this.props}
            onFocusIn={this.onFocusIn}
            onMouseEnter={this.onMouseIn}
            fastDisabled={
              !this.focusInControlGroup && this.fastDisabledCallback
            }
          />
        )}
      </div>
    );
  }

  private isFastDisabledActive = () => {
    return (
      (typeof this.props.fastDisabled === "function"
        ? this.props.fastDisabled()
        : this.props.fastDisabled) && !this.focusInControlGroup
    );
  };

  // A quick callback passed to the individual items (components)
  // to be used in mapStateToProps while being able to keep the same reference
  // and hence not cause unnecessary rerenders
  private fastDisabledCallback = () => {
    return this.isFastDisabled;
  };

  private onFocusIn = () => {
    // If we were given a onFocusIn callback, call it first
    this.props.onFocusIn?.();

    // If fast disable mode is currently active
    // and focus is coming into the control group (via keyboard),
    // we need to turn off fast disable to allow for the correct
    // keyboard interaction/markup with/for the controls
    if (this.isFastDisabledActive()) {
      this.focusInControlGroup = true;
      this.forceUpdate();
    }
  };

  // If fast disable mode is currently active
  // and mouse is coming into the control group (via hover),
  // we need to turn off fast disable to allow for the correct
  // mouse interaction/markup with/for the controls. This
  // is to support tooltips on hover of disabled controls
  private onMouseIn = this.async.debounce(() => {
    const allowMouseIn = this.context.isExperimentalFeatureEnabled?.(
      ribbonExperiments.isDisabledTooltipsEnabled
    );
    if (allowMouseIn && this.isFastDisabledActive()) {
      this.focusInControlGroup = true;
      this.forceUpdate();
    }
  }, 100);

  public focus() {
    if (this.group.current) {
      this.group.current.focus();
    }
  }

  public hasContainer(): boolean {
    if (this.group.current) {
      return this.group.current.hasContainer();
    }

    return false;
  }

  public containsFocus(): boolean {
    if (this.group.current) {
      return this.group.current.containsFocus();
    }

    return false;
  }
}
